import { useState } from 'react'
import { ENCRYPTED_STORAGE_TOKEN } from '../constants'
import { decryptToken } from '../utils/crypto'
import { setSanityToken } from '../utils/sanity'

type PasswordDialogProps = {
  isOpen: boolean
  onClose: () => void
  onAuthenticated: (token: string) => void
}

export function PasswordDialog({ isOpen, onClose, onAuthenticated }: PasswordDialogProps) {
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isChecking, setIsChecking] = useState(false)

  if (!isOpen) return null

  const handleOk = async () => {
    setError(null)
    setIsChecking(true)
    try {
      const token = await decryptToken(ENCRYPTED_STORAGE_TOKEN, password)
      if (token) {
        setSanityToken(token)
        setPassword('')
        onAuthenticated(token)
      } else {
        setError('Incorrect password')
      }
    } finally {
      setIsChecking(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && password) {
      handleOk()
    }
  }

  const handleClose = () => {
    setPassword('')
    setError(null)
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-cream rounded-lg shadow-xl w-full max-w-sm">
        <div className="p-6">
          <h2 className="text-lg font-bold mb-4">Enter password to save</h2>

          <input
            type="password"
            value={password}
            onChange={(e) => { setPassword(e.target.value); setError(null) }}
            onKeyDown={handleKeyDown}
            placeholder="Password"
            autoFocus
            autoComplete="off"
            data-1p-ignore
            className="w-full px-3 py-2 border border-black/20 rounded bg-white focus:outline-none focus:border-black"
          />

          <p className={`mt-2 text-sm text-red-600 ${error ? 'visible' : 'invisible'}`}>
            {error ?? 'Incorrect password'}
          </p>

          <div className="flex justify-end gap-2 mt-4">
            <button
              onClick={handleClose}
              className="px-4 py-2 bg-black/10 rounded hover:bg-black/20"
            >
              Cancel
            </button>
            <button
              onClick={handleOk}
              disabled={!password || isChecking}
              className="px-4 py-2 bg-black text-cream rounded hover:bg-black/80 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isChecking ? 'Checking...' : 'OK'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
